import _ from "lodash";

export function normalizeIdentityId(value) {
	return _.toString(value || "").trim();
}

export function getInventoryEntryOwnerIdentityId(activity = {}) {
	return normalizeIdentityId(
		_.get(activity, "identityId", "") ||
		_.get(activity, "ownerIdentityId", "") ||
		_.get(activity, "ownerId", "") ||
		_.get(activity, "sharedArtifactOwnerId", "")
	);
}

export function getOrgMemberIdentityIds(selectedOrg = {}, currentIdentityId = "") {
	const memberIdsFromMap = _.keys(_.get(selectedOrg, "membersMap", {}));
	const memberIdsFromArray = _.flatMap(_.get(selectedOrg, "members", []), (member) => [
		_.get(member, "id", ""),
		_.get(member, "userSubId", ""),
		_.get(member, "sub", ""),
		_.get(member, "identityId", "")
	]);
	const selectedMemberIds = [
		_.get(selectedOrg, "member.id", ""),
		_.get(selectedOrg, "member.userSubId", ""),
		_.get(selectedOrg, "member.sub", ""),
		_.get(selectedOrg, "member.identityId", "")
	];

	return _.uniq([]
		.concat(memberIdsFromMap, memberIdsFromArray, selectedMemberIds, [
			_.get(selectedOrg, "id", ""),
			currentIdentityId
		])
		.map(normalizeIdentityId)
			.filter(Boolean));
}

function isTruthyRoleValue(value) {
	return value === true || value === 1 || value === "1" || _.toString(value).toLowerCase() === "true";
}

export function isOrgContentCapableMember(member = {}) {
	return isTruthyRoleValue(_.get(member, "administrator", false)) || isTruthyRoleValue(_.get(member, "author", false));
}

export function getOrgContentCapableIdentityIds(selectedOrg = {}, currentIdentityId = "") {
	const membersFromArray = _.get(selectedOrg, "members", []);
	const membersFromMap = _.values(_.get(selectedOrg, "membersMap", {}));
	const selectedMember = _.get(selectedOrg, "member", null);
	const contentMembers = _.uniqBy([]
		.concat(membersFromArray, membersFromMap, selectedMember ? [selectedMember] : [])
		.filter((member) => member && isOrgContentCapableMember(member)), (member) => (
		normalizeIdentityId(_.get(member, "userSubId", "")) ||
		normalizeIdentityId(_.get(member, "identityId", "")) ||
		normalizeIdentityId(_.get(member, "sub", "")) ||
		normalizeIdentityId(_.get(member, "id", ""))
	));
	const contentMemberIds = _.flatMap(contentMembers, (member) => [
		_.get(member, "userSubId", ""),
		_.get(member, "identityId", ""),
		_.get(member, "sub", "")
	]);

	return _.uniq([]
		.concat(contentMemberIds, [
			_.get(selectedOrg, "id", ""),
			currentIdentityId
		])
		.map(normalizeIdentityId)
		.filter(Boolean));
}

export function hasLoadedOrgMemberDirectory(selectedOrg = {}) {
	return _.keys(_.get(selectedOrg, "membersMap", {})).length > 0 ||
		_.get(selectedOrg, "members", []).length > 0;
}

export function buildInventoryIntegrityContext(context = {}) {
	const selectedOrg = context.selectedOrg || {};
	const currentIdentityId = normalizeIdentityId(context.currentIdentityId);
	const orgId = normalizeIdentityId(context.orgId || _.get(selectedOrg, "id", ""));
	const validOwnerIds = getOrgMemberIdentityIds(selectedOrg, currentIdentityId);

	return {
		...context,
		selectedOrg,
		currentIdentityId,
		orgId,
		validOwnerIds,
		validOwnerIdSet: new Set(validOwnerIds),
		hasLoadedMemberDirectory: hasLoadedOrgMemberDirectory(selectedOrg)
	};
}

function normalizeOrgId(value) {
	const orgId = normalizeIdentityId(value).toLowerCase();
	return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(orgId) ? orgId : "";
}

export function getInventoryEntryOrgId(activity = {}) {
	return normalizeOrgId(_.get(activity, "orgId", ""));
}

function hasValidInventoryOwnerId(ownerIdentityId, context = {}, selectedOrg = {}, currentIdentityId = "") {
	if (context.validOwnerIdSet && _.isFunction(context.validOwnerIdSet.has)) {
		return context.validOwnerIdSet.has(ownerIdentityId);
	}

	if (_.isArray(context.validOwnerIds)) {
		return context.validOwnerIds.indexOf(ownerIdentityId) !== -1;
	}

	return getOrgMemberIdentityIds(selectedOrg, currentIdentityId).indexOf(ownerIdentityId) !== -1;
}

export function isAllowedPublicInventoryOwner(ownerIdentityId, orgId = "") {
	const owner = normalizeIdentityId(ownerIdentityId);
	const currentOrgId = normalizeIdentityId(orgId);

	return owner === "public" || (currentOrgId !== "" && owner === `${currentOrgId}/public`);
}

export function isForeignInventoryOwner(activity = {}, context = {}) {
	const ownerIdentityId = getInventoryEntryOwnerIdentityId(activity);
	if (!ownerIdentityId) {
		return false;
	}

	const currentIdentityId = normalizeIdentityId(context.currentIdentityId);
	const selectedOrg = context.selectedOrg || {};
	const orgId = normalizeIdentityId(context.orgId || _.get(selectedOrg, "id", ""));
	if (isAllowedPublicInventoryOwner(ownerIdentityId, orgId)) {
		return false;
	}

	if (hasValidInventoryOwnerId(ownerIdentityId, context, selectedOrg, currentIdentityId)) {
		return false;
	}

	if (ownerIdentityId.indexOf("/") !== -1) {
		return true;
	}

	const hasLoadedMemberDirectory = _.has(context, "hasLoadedMemberDirectory")
		? context.hasLoadedMemberDirectory
		: hasLoadedOrgMemberDirectory(selectedOrg);

	// Without member context, fail open so we do not hide legitimate same-org shares.
	return hasLoadedMemberDirectory;
}

export function getInventoryEntryIntegrityIssue(activity = {}, context = {}) {
	if (!activity || activity.newDoc) {
		return null;
	}

	const hasValidId = _.isString(activity.id) && activity.id.trim() !== "";
	const hasValidName = _.isString(activity.name) && activity.name.trim() !== "";

	if (!hasValidId) {
		return {
			key: "missingId",
			label: "Missing activity id."
		};
	}

	if (!hasValidName) {
		return {
			key: "missingName",
			label: "Missing activity name."
		};
	}

	if (isForeignInventoryOwner(activity, context)) {
		const ownerIdentityId = getInventoryEntryOwnerIdentityId(activity);
		return {
			key: "foreignOwner",
			label: `Owner identity ${ownerIdentityId} is not part of the selected organization.`
		};
	}

	const currentOrgId = normalizeOrgId(context.orgId || _.get(context, "selectedOrg.id", ""));
	const inventoryOrgId = getInventoryEntryOrgId(activity);
	if (currentOrgId && inventoryOrgId && inventoryOrgId !== currentOrgId) {
		return {
			key: "foreignOrg",
			label: `Inventory org ${inventoryOrgId} does not match selected org ${currentOrgId}.`
		};
	}

	return null;
}

export function filterForeignInventoryEntries(activities = [], context = {}) {
	const kept = [];
	const removed = [];

	(_.isArray(activities) ? activities : []).forEach((activity) => {
		const issue = getInventoryEntryIntegrityIssue(activity, context);
		if (issue && (issue.key === "foreignOwner" || issue.key === "foreignOrg")) {
			removed.push({ activity, issue });
		} else {
			kept.push(activity);
		}
	});

	return { kept, removed };
}
